// Search in the JSON browser (components/common/JSONBrowser.tsx). The text someone types is
// matched as written, never as a regular expression: "a+b", "(" or "[" in a search would
// otherwise throw and take the view down with it.

type JSONPrimitive = string | number | boolean | null;
type JSONValue = JSONPrimitive | JSONValue[] | { [key: string]: JSONValue };

function isPrimitive(value: JSONValue): value is JSONPrimitive {
  return value === null || typeof value !== 'object';
}

/** Characters with a meaning in a regular expression, escaped to match themselves. */
export function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Splits text around each match of the search, keeping the matches, for highlighting. */
export function splitOnSearchTerm(value: string, search_term: string): string[] {
  return value.split(new RegExp(`(${escapeRegExp(search_term)})`, 'g'));
}

/**
 * Which keys and values match the search, as a tree shaped like the JSON: `true` for a
 * value or key that matches, an object of the matching children otherwise, and undefined
 * where nothing does. A list's items are values, even when an item is itself a list.
 */
export function buildSearchResults(
  search_term: string,
  json: JSONValue,
): object | boolean | undefined {
  if (isPrimitive(json)) return String(json).includes(search_term) ? true : undefined;
  const results: Record<number, object | boolean> = {};
  let found_one = false;
  const is_list = Array.isArray(json);
  const entries: [string | null, JSONValue][] = is_list
    ? json.map((item) => [null, item])
    : Object.entries(json);
  entries.forEach(([key, value], index) => {
    if (key !== null && key.includes(search_term)) {
      results[index] = true;
      found_one = true;
    }
    const child = buildSearchResults(search_term, value);
    if (child) {
      results[index] = child;
      found_one = true;
    }
  });
  return found_one ? results : undefined;
}
